import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Share2, MessageCircle, Copy, Languages } from "lucide-react";
import api from "../lib/api";
import { waLink } from "../lib/clinic";
import { toast } from "sonner";
import SafeImage from "../components/site/SafeImage";
import ReadingProgress from "../components/site/ReadingProgress";
import AnswerSummary from "../components/site/AnswerSummary";
import ReviewedBadge from "../components/site/ReviewedBadge";
import TableOfContents from "../components/site/TableOfContents";
import RelatedTreatments from "../components/site/RelatedTreatments";
import RelatedArticles from "../components/site/RelatedArticles";
import PopularServices from "../components/site/PopularServices";
import RichContent from "../components/site/RichContent";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { AUTHOR_REF, PUBLISHER_REF, SITE, SPEAKABLE, breadcrumb, graph, authorForPost } from "../lib/schema";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Language: read `?lang=hi` (default: en). Only honour hi if the post ships a
  // Hindi translation — otherwise silently fall back to English so shared links
  // never render a blank article.
  const requestedLang = (searchParams.get("lang") || "en").toLowerCase();
  const hindiAvailable = !!(post?.has_hindi && (post?.hindi_body || post?.hindi_title));
  const lang = requestedLang === "hi" && hindiAvailable ? "hi" : "en";

  // Choose the fields to render based on the active language.
  const displayTitle = lang === "hi" ? (post?.hindi_title || post?.title) : post?.title;
  const displayExcerpt = lang === "hi" ? (post?.hindi_excerpt || post?.excerpt) : post?.excerpt;
  const displayBody = lang === "hi" ? (post?.hindi_body || post?.body) : post?.body;
  const displayAnswer = lang === "hi" ? (post?.hindi_answer_summary || post?.answer_summary) : post?.answer_summary;

  const canonicalPath = lang === "hi" ? `/blog/${slug}?lang=hi` : `/blog/${slug}`;

  const alternates = useMemo(() => {
    if (!post) return undefined;
    if (!post.has_hindi) return undefined;
    return [
      { hreflang: "en-IN", href: `/blog/${slug}` },
      { hreflang: "hi-IN", href: `/blog/${slug}?lang=hi` },
      { hreflang: "x-default", href: `/blog/${slug}` },
    ];
  }, [post, slug]);

  usePageMeta({
    title: post ? `${displayTitle} | Dr. Shantanu Kumar Gupta` : "Blog Post | Dr. Shantanu Kumar Gupta",
    path: canonicalPath,
    description: displayAnswer || displayExcerpt,
    alternates,
  });

  const switchLang = (target) => {
    const next = new URLSearchParams(searchParams);
    if (target === "hi") next.set("lang", "hi");
    else next.delete("lang");
    setSearchParams(next, { replace: false });
  };

  const articleSchema = useMemo(() => {
    if (!post) return null;
    const url = lang === "hi" ? `${SITE}/blog/${slug}?lang=hi` : `${SITE}/blog/${slug}`;
    return graph([
      {
        "@type": ["BlogPosting", "Article"],
        "@id": `${url}#article`,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        speakable: SPEAKABLE,
        headline: displayTitle,
        description: displayAnswer || displayExcerpt,
        image: post.cover_image,
        datePublished: post.updated_at,
        dateModified: post.medically_reviewed || post.updated_at,
        author: authorForPost(post),
        publisher: PUBLISHER_REF,
        articleSection: post.category,
        keywords: (post.tags || []).join(", "),
        inLanguage: lang === "hi" ? "hi-IN" : "en-IN",
        wordCount: (displayBody || "").split(/\s+/).length,
        ...(post.medically_reviewed
          ? {
              reviewedBy: AUTHOR_REF,
              lastReviewed: post.medically_reviewed,
            }
          : {}),
      },
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: displayTitle, path: canonicalPath },
      ]),
    ]);
  }, [post, slug, lang, displayTitle, displayAnswer, displayExcerpt, displayBody, canonicalPath]);
  usePageJsonLd(articleSchema);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => {
      // If backend resolved a legacy slug, replace-navigate to the canonical URL.
      if (r.data?.canonical_slug && r.data.canonical_slug !== slug) {
        navigate(`/blog/${r.data.canonical_slug}`, { replace: true });
        return;
      }
      setPost(r.data);
    }).catch(() => setNotFound(true));
  }, [slug, navigate]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: displayTitle, url }); } catch { /* ignore cancel */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  if (notFound) return <div className="container-page py-32 text-center">Post not found. <Link to="/blog" className="text-[hsl(var(--primary))]">Back to blog</Link></div>;
  if (!post) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <article data-testid="blog-post-page" lang={lang === "hi" ? "hi-IN" : "en-IN"}>
      <ReadingProgress />
      {/* Breadcrumb */}
      <section className="pt-14 container-page">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
          <ArrowLeft size={14} /> {lang === "hi" ? "ब्लॉग" : "Blog"}
        </Link>
      </section>

      {/* Hero — title + meta, uses the wide container so it aligns with body below */}
      <section className="pt-6 pb-10 container-page max-w-5xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="overline mb-4">{post.category}</div>
          {hindiAvailable && (
            <div
              className="inline-flex items-center rounded-full border border-border p-0.5 text-xs bg-background/60 backdrop-blur"
              role="tablist"
              aria-label="Article language"
              data-testid="blog-lang-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={lang === "en"}
                onClick={() => switchLang("en")}
                className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${lang === "en" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="blog-lang-en"
              >
                <Languages size={12} /> EN
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={lang === "hi"}
                onClick={() => switchLang("hi")}
                className={`px-3 py-1.5 rounded-full transition-colors ${lang === "hi" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="blog-lang-hi"
              >
                हिन्दी
              </button>
            </div>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
          {displayTitle}
        </h1>
        <ReviewedBadge reviewed={post.medically_reviewed} readingTime={post.reading_time} />
      </section>

      {/* Cover image */}
      {post.cover_image && (
        <section className="container-page">
          <SafeImage
            src={post.cover_image}
            alt={displayTitle}
            label={displayTitle}
            className="w-full max-h-[560px] rounded-2xl border border-border"
            style={{ aspectRatio: "16/9" }}
          />
        </section>
      )}

      {/* Body — 3 column magazine layout on desktop */}
      <section className="py-14 container-page">
        <div className="grid gap-10 lg:gap-14 lg:grid-cols-[220px_minmax(0,1fr)_260px]">
          {/* Left rail — sticky TOC */}
          <div className="hidden lg:block">
            <TableOfContents containerSelector="[data-testid=blog-body]" />
          </div>

          {/* Center — the article body */}
          <div data-testid="blog-body" className="max-w-[760px] mx-auto lg:mx-0 w-full">
            <AnswerSummary text={displayAnswer} />
            <p className="text-lg font-serif italic text-[hsl(var(--muted-foreground))] mt-8">
              {displayExcerpt}
            </p>
            <div className="mt-4"><RichContent text={displayBody || ""} /></div>

            {post.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground">
                    #{t}
                  </span>
                ))}
              </div>
            )}

            <RelatedTreatments slugs={post.related_expertise} />
            <MedicalDisclaimer />

            <div className="mt-10 border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
              <div className="font-serif text-xl">{lang === "hi" ? "इस लेख के बारे में प्रश्न है?" : "Have a question about this article?"}</div>
              <p className="mt-2 text-sm text-muted-foreground">
                {lang === "hi"
                  ? "डॉ. शांतनु हर पूछताछ की व्यक्तिगत रूप से समीक्षा करते हैं — आमतौर पर कुछ ही घंटों में।"
                  : "Dr. Shantanu personally reviews every enquiry — usually within a few hours."}
              </p>
              <a
                href={waLink(lang === "hi"
                  ? `नमस्ते डॉ. शांतनु, मैंने आपका लेख पढ़ा: ${displayTitle}`
                  : `Hi Dr. Shantanu, I read your blog post: ${displayTitle}`)}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn-primary mt-4"
                data-testid="blog-book-btn"
              >
                <MessageCircle size={16} /> {lang === "hi" ? "व्हाट्सएप पर संदेश भेजें" : "Message on WhatsApp"}
              </a>
            </div>
          </div>

          {/* Right rail — sticky author + share (desktop only) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-border p-5 bg-[hsl(var(--secondary))]/40" data-testid="side-author-card">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Written by</div>
                <div className="font-serif text-lg leading-snug">Dr. Shantanu Kumar Gupta</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                  MBBS · MS · DNB · FICO
                </div>
                <p className="mt-3 text-sm text-[hsl(var(--foreground))]/75 leading-relaxed">
                  Cataract, cornea & medical retina specialist practising at Shantanu Netralaya, Varanasi.
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-block text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]"
                  data-testid="side-author-more"
                >
                  More about Dr. Shantanu →
                </Link>
              </div>

              <div className="rounded-2xl border border-border p-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Share</div>
                <div className="flex gap-2">
                  <button
                    onClick={share}
                    className="btn-ghost !py-2 !px-3 text-xs flex-1"
                    data-testid="blog-share"
                  >
                    <Share2 size={13} /> Share
                  </button>
                  <button
                    onClick={copyLink}
                    className="btn-ghost !py-2 !px-3 text-xs flex-1"
                    data-testid="blog-copy-link"
                  >
                    <Copy size={13} /> Copy link
                  </button>
                </div>
              </div>

              <a
                href={waLink(`Hi Dr. Shantanu, I read your article: ${post.title}`)}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="block rounded-2xl border border-[hsl(var(--accent))]/40 p-5 bg-[hsl(var(--accent))]/5 hover:bg-[hsl(var(--accent))]/10 transition-colors"
                data-testid="side-book-cta"
              >
                <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-2">Book a consult</div>
                <div className="font-serif text-lg leading-snug">Talk to Dr. Shantanu on WhatsApp</div>
                <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                  <MessageCircle size={13} /> Start chat
                </div>
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Full-width author card below body (mobile + tablet only) */}
      <section className="container-page lg:hidden">
        <AuthorCard post={post} />
      </section>

      <RelatedArticles
        kind="blog"
        currentSlug={post.slug}
        category={post.category}
        explicitSlugs={post.related_articles || []}
      />

      <PopularServices post={post} />
    </article>
  );
};

export default BlogPost;
